/**
 * i18n Hooks
 *
 * Hooks customizados para internacionalizacao.
 * Encapsula react-i18next com funcionalidades extras para multi-tenant.
 *
 * @since 2025-12-16
 * @epic INIT-0045-03
 *
 * @example
 * // Uso basico
 * const { t } = useTmfTranslation();
 * return <h1>{t('welcome')}</h1>;
 *
 * @example
 * // Com namespace de modulo
 * const { t } = useTmfTranslation('modules/person');
 * return <span>{t('name')}</span>;
 *
 * @example
 * // Multiplos namespaces
 * const { t } = useTmfTranslation(['common', 'modules/person']);
 * return <span>{t('modules/person:name')}</span>;
 */

import { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './config';
import { supportedLanguages, type SupportedLanguageCode } from './config';
import { i18nApi } from '@/services/i18nApi';

// ==========================================
// TYPES
// ==========================================

export interface UseTmfTranslationOptions {
  /** Carregar overrides do tenant */
  loadTenantOverrides?: boolean;
  /** Key prefix (ns ou namespace) */
  keyPrefix?: string;
}

export interface UseTmfTranslationReturn {
  /** Funcao de traducao */
  t: (key: string, params?: Record<string, string | number>, defaultValue?: string) => string;
  /** Idioma atual */
  language: string;
  /** Mudar idioma */
  changeLanguage: (lang: SupportedLanguageCode) => Promise<void>;
  /** Idiomas disponiveis */
  languages: typeof supportedLanguages;
  /** Loading state */
  isLoading: boolean;
  /** Ready state */
  isReady: boolean;
  /** Instancia do i18n */
  i18n: typeof i18n;
}

// ==========================================
// MAIN HOOK
// ==========================================

/**
 * Hook principal para traducoes TMF
 *
 * @param ns - Namespace(s) a carregar (default: 'common')
 * @param options - Opcoes adicionais
 */
export function useTmfTranslation(
  ns: string | string[] = 'common',
  options?: UseTmfTranslationOptions
): UseTmfTranslationReturn {
  const { t: i18nT, i18n: i18nInstance, ready } = useTranslation(ns, {
    keyPrefix: options?.keyPrefix,
  });
  const [isLoading, setIsLoading] = useState(!ready);

  // Atualizar loading quando ready mudar
  useEffect(() => {
    setIsLoading(!ready);
  }, [ready]);

  // Carregar overrides do tenant (se habilitado)
  useEffect(() => {
    if (options?.loadTenantOverrides) {
      loadTenantOverrides(Array.isArray(ns) ? ns : [ns]);
    }
  }, [options?.loadTenantOverrides, ns]);

  // Funcao t() com suporte a defaultValue
  const t = useCallback(
    (key: string, params?: Record<string, string | number>, defaultValue?: string): string => {
      const result = i18nT(key, params || {});

      // Se a chave nao foi encontrada e tem defaultValue, retornar defaultValue
      if (result === key && defaultValue !== undefined) {
        return defaultValue;
      }

      return result;
    },
    [i18nT]
  );

  // Mudar idioma
  const changeLanguage = useCallback(
    async (lang: SupportedLanguageCode): Promise<void> => {
      setIsLoading(true);
      await i18nInstance.changeLanguage(lang);
      localStorage.setItem('tmf_language', lang);
      setIsLoading(false);
    },
    [i18nInstance]
  );

  return {
    t,
    language: i18nInstance.language,
    changeLanguage,
    languages: supportedLanguages,
    isLoading,
    isReady: ready,
    i18n: i18nInstance,
  };
}

// ==========================================
// MODULE HOOK
// ==========================================

/**
 * Hook para traducoes de um modulo especifico
 *
 * @param moduleName - Nome do modulo (ex: 'person', 'product')
 * @param additionalNs - Namespaces adicionais a carregar
 */
export function useModuleTranslation(
  moduleName: string,
  additionalNs: string[] = ['common']
) {
  const namespaces = [`modules/${moduleName}`, ...additionalNs];
  return useTmfTranslation(namespaces);
}

// ==========================================
// DYNAMIC LANGUAGES HOOK
// ==========================================

interface DynamicLanguage {
  code: string;
  name: string;
  flag: string;
}

// Cache global para idiomas dinamicos
let cachedDynamicLanguages: DynamicLanguage[] | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

/**
 * Hook para buscar idiomas ativos da API
 * Usa cache para evitar chamadas excessivas
 */
export function useDynamicLanguages() {
  const [languages, setLanguages] = useState<DynamicLanguage[]>(cachedDynamicLanguages || []);
  const [loading, setLoading] = useState(!cachedDynamicLanguages);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLanguages = async () => {
      // Usar cache se ainda valido
      if (cachedDynamicLanguages && Date.now() - cacheTimestamp < CACHE_TTL) {
        setLanguages(cachedDynamicLanguages);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await i18nApi.getLanguages(true); // apenas ativos

        // Mapear para formato esperado pelo LanguageSelector
        const flagMap: Record<string, string> = {
          'pt': '\u{1F1E7}\u{1F1F7}',
          'en': '\u{1F1FA}\u{1F1F8}',
          'es': '\u{1F1EA}\u{1F1F8}',
          'fr': '\u{1F1EB}\u{1F1F7}',
          'de': '\u{1F1E9}\u{1F1EA}',
          'it': '\u{1F1EE}\u{1F1F9}',
          'ja': '\u{1F1EF}\u{1F1F5}',
          'zh': '\u{1F1E8}\u{1F1F3}',
          'ko': '\u{1F1F0}\u{1F1F7}',
          'ar': '\u{1F1F8}\u{1F1E6}',
        };

        const mapped: DynamicLanguage[] = response.languages.map((lang) => {
          // Converter formato da API (pt_br) para i18n (pt-BR)
          const code = lang.language_code?.replace('_', '-').replace(/^([a-z]{2})-([a-z]{2})$/i,
            (_, p1, p2) => `${p1.toLowerCase()}-${p2.toUpperCase()}`
          ) || 'pt-BR';
          const shortCode = code.split('-')[0].toLowerCase();

          return {
            code,
            name: lang.language_name || code,
            flag: flagMap[shortCode] || '\u{1F3F3}\u{FE0F}',
          };
        });

        // Atualizar cache
        cachedDynamicLanguages = mapped;
        cacheTimestamp = Date.now();

        setLanguages(mapped);
        setError(null);
      } catch (err) {
        // Silenciar erro - fallback para idiomas estáticos é esperado
        // quando a API i18n não está rodando
        // Log removido para não poluir console - comportamento esperado em dev
        setError(null); // Não mostrar erro ao usuário
        // Fallback para idiomas estaticos
        setLanguages(supportedLanguages.map(l => ({
          code: l.code,
          name: l.name,
          flag: l.flag,
        })));
      } finally {
        setLoading(false);
      }
    };

    fetchLanguages();
  }, []);

  return { languages, loading, error };
}

// ==========================================
// LANGUAGE HOOK
// ==========================================

interface UseLanguageOptions {
  /** Usar idiomas dinamicos da API (default: true) */
  useDynamic?: boolean;
}

/**
 * Hook para gerenciar apenas o idioma (sem traducoes)
 *
 * @param options - Opcoes do hook
 */
export function useLanguage(options: UseLanguageOptions = {}) {
  const { useDynamic = true } = options;
  const { i18n: i18nInstance } = useTranslation();
  const [language, setLanguageState] = useState(i18nInstance.language);

  // Buscar idiomas dinamicos se habilitado
  const { languages: dynamicLanguages, loading: dynamicLoading } = useDynamicLanguages();

  // Sincronizar estado com i18n
  useEffect(() => {
    const handleLanguageChange = (lang: string) => {
      setLanguageState(lang);
    };

    i18nInstance.on('languageChanged', handleLanguageChange);
    return () => {
      i18nInstance.off('languageChanged', handleLanguageChange);
    };
  }, [i18nInstance]);

  const setLanguage = useCallback(
    async (lang: SupportedLanguageCode | string) => {
      await i18nInstance.changeLanguage(lang);
      localStorage.setItem('tmf_language', lang);
    },
    [i18nInstance]
  );

  // Usar idiomas dinamicos ou estaticos
  const languages = useMemo(() => {
    if (useDynamic && dynamicLanguages.length > 0) {
      return dynamicLanguages;
    }
    return supportedLanguages.map(l => ({
      code: l.code,
      name: l.name,
      flag: l.flag,
    }));
  }, [useDynamic, dynamicLanguages]);

  return {
    language,
    setLanguage,
    languages,
    isDefault: language === 'pt-BR',
    isLoading: dynamicLoading,
  };
}

// ==========================================
// FORMAT HOOKS
// ==========================================

/**
 * Hook para formatacao de datas
 */
export function useDateFormat() {
  const { language } = useLanguage();
  const { t } = useTranslation('dates');

  const formatDate = useCallback(
    (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
      const d = typeof date === 'string' ? new Date(date) : date;
      return d.toLocaleDateString(language, options);
    },
    [language]
  );

  const formatTime = useCallback(
    (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
      const d = typeof date === 'string' ? new Date(date) : date;
      return d.toLocaleTimeString(language, options);
    },
    [language]
  );

  const formatDateTime = useCallback(
    (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
      const d = typeof date === 'string' ? new Date(date) : date;
      return d.toLocaleString(language, options);
    },
    [language]
  );

  const formatRelative = useCallback(
    (date: Date | string): string => {
      const d = typeof date === 'string' ? new Date(date) : date;
      const now = new Date();
      const diffMs = now.getTime() - d.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return t('today');
      if (diffDays === 1) return t('yesterday');
      if (diffDays < 7) return t('daysAgo', { count: diffDays });
      if (diffDays < 30) return t('weeksAgo', { count: Math.floor(diffDays / 7) });
      if (diffDays < 365) return t('monthsAgo', { count: Math.floor(diffDays / 30) });
      return t('yearsAgo', { count: Math.floor(diffDays / 365) });
    },
    [t]
  );

  return {
    formatDate,
    formatTime,
    formatDateTime,
    formatRelative,
    language,
  };
}

/**
 * Hook para formatacao de numeros
 */
export function useNumberFormat() {
  const { language } = useLanguage();

  const formatNumber = useCallback(
    (num: number, options?: Intl.NumberFormatOptions): string => {
      return new Intl.NumberFormat(language, options).format(num);
    },
    [language]
  );

  const formatCurrency = useCallback(
    (value: number, currency: string = 'BRL'): string => {
      return new Intl.NumberFormat(language, {
        style: 'currency',
        currency,
      }).format(value);
    },
    [language]
  );

  const formatPercent = useCallback(
    (value: number, decimals: number = 2): string => {
      return new Intl.NumberFormat(language, {
        style: 'percent',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value);
    },
    [language]
  );

  return {
    formatNumber,
    formatCurrency,
    formatPercent,
    language,
  };
}

// ==========================================
// TENANT OVERRIDES
// ==========================================

/**
 * Carrega overrides de traducao do tenant
 * @internal
 */
async function loadTenantOverrides(namespaces: string[]): Promise<void> {
  try {
    const overrides = await i18nApi.getTenantOverrides();

    for (const ns of namespaces) {
      if (overrides[ns] && Object.keys(overrides[ns]).length > 0) {
        i18n.addResourceBundle(i18n.language, ns, overrides[ns], true, true);
      }
    }

    if (import.meta.env.DEV && Object.keys(overrides).length > 0) {
      console.debug('[i18n] Overrides carregados:', Object.keys(overrides));
    }
  } catch (error) {
    // Graceful: nao quebrar o boot do app por overrides ausentes
    if (import.meta.env.DEV) console.warn('[i18n] Erro ao carregar overrides do tenant:', error);
  }
}

// ==========================================
// EXPORTS
// ==========================================

export default useTmfTranslation;
